import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import { AgGridReact } from "ag-grid-react";

import Link from "@material-ui/core/Link";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import LinkIcon from "@material-ui/icons/Link";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import CloseIcon from "@material-ui/icons/Close";

import { fetchPayoutById } from "../../../redux/features/sales/payoutsSlice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  infoGrid: {
    marginTop: theme.spacing(1),
  },
  labelText: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: theme.spacing(0.5),
  },
  valueText: {
    fontWeight: 600,
    fontSize: "1rem",
  },
  transferValue: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: "#19a86d",
  },
  commissionValue: {
    fontWeight: 700,
    fontSize: "1.25rem",
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  textWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
}));

const fmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const InfoField = ({ label, value, className }) => {
  const classes = useStyles();
  return (
    <div>
      <Typography className={classes.labelText}>{label}</Typography>
      <Typography className={className || classes.valueText}>
        {value || "—"}
      </Typography>
    </div>
  );
};

const PayoutView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });

  const { mode } = useSelector((state) => state.ui);
  const { currentPayout, isLoading, error } = useSelector(
    (state) => state.payouts
  );

  useEffect(() => {
    dispatch(fetchPayoutById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (location.state?.showSuccess) {
      setSnackbar({
        open: true,
        message:
          location.state.snackbarContent || "Payout created successfully!",
      });
      history.replace(location.pathname, {});
    }
  }, [history, location]);

  const payout = currentPayout?.payout;
  const orders = useMemo(() => currentPayout?.orders || [], [currentPayout]);

  const handleExportPdf = useCallback(() => {
    if (!payout) return;
    const doc = new jsPDF({ orientation: "landscape" });
    const isCondoPdf = payout.payment_type === "condo";
    const commissionPct =
      payout.hotel_commission ||
      payout.condo_commission ||
      payout.metadata?.commission_pct;

    doc.setFontSize(16);
    doc.text(`EnjoySPI Payout #${payout.payment_id}`, 14, 18);

    doc.setFontSize(10);
    const col1x = 14;
    const col2x = 110;
    let y = 28;

    const line = (label, value, x) => {
      doc.setTextColor(120);
      doc.text(label, x, y);
      doc.setTextColor(0);
      doc.text(String(value ?? "—"), x + 40, y);
    };

    line(
      "Date:",
      payout.created
        ? moment(payout.created).tz("America/Chicago").format("lll")
        : "—",
      col1x
    );
    line("Commission Rate:", `${commissionPct}%`, col2x);
    y += 7;
    line("Orders:", orders.length, col1x);
    line("Payment Type:", payout.payment_type?.toUpperCase() ?? "—", col2x);
    y += 7;
    line("Status:", payout.payment_status?.toUpperCase() ?? "—", col1x);
    if (payout.stripe_transfer_id) {
      line("Stripe Transfer ID:", payout.stripe_transfer_id, col2x);
    }
    y += 7;
    line(
      isCondoPdf ? "Condo:" : "Hotel:",
      (isCondoPdf ? payout.condo_name : payout.hotel_name) ?? "—",
      col1x
    );
    line("Bank Account:", payout.stripe_account_name ?? "—", col2x);
    y += 7;
    if (payout.stripe_account_id) {
      line("Stripe Account ID:", payout.stripe_account_id, col1x);
      doc.setTextColor(0, 100, 200);
      doc.textWithLink("View on Stripe", col2x, y, {
        url: `https://dashboard.stripe.com/connect/accounts/${payout.stripe_account_id}`,
      });
      doc.setTextColor(0);
      y += 7;
    }
    if (payout.comments) {
      line("Comments:", payout.comments, col1x);
      y += 7;
    }
    y += 3;
    doc.setDrawColor(200);
    doc.line(14, y, 283, y);
    y += 7;

    doc.setFontSize(11);
    doc.setTextColor(0);
    const summaryItems = [
      ["Orders Total", fmt.format(Number(payout.total_orders_amount))],
      ["EnjoySPI Commission", fmt.format(Number(payout.commission_amount))],
      ["Transfer Amount", fmt.format(Number(payout.total_paid))],
    ];
    summaryItems.forEach(([label, value], i) => {
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(label.toUpperCase(), 14 + i * 90, y);
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(value, 14 + i * 90, y + 6);
    });
    y += 16;

    const pdfHead = isCondoPdf
      ? [
          [
            "Order ID",
            "Customer",
            "Date Placed",
            "Condo",
            "Check In",
            "Check Out",
            "Taxable Base",
            "Commission",
            "Taxes",
            "Transfer Amount",
          ],
        ]
      : [
          [
            "Order ID",
            "Customer",
            "Date Placed",
            "Package",
            "Room",
            "Check In",
            "Check Out",
            "Taxable Base",
            "Commission",
            "Taxes",
            "Resort Fee",
            "Order Total",
            "Transfer Amount",
          ],
        ];

    const pdfBody = isCondoPdf
      ? orders.map((o) => [
          o.id,
          o.customer_name ?? "",
          o.date_order_placed
            ? moment(o.date_order_placed).tz("America/Chicago").format("lll")
            : "",
          o.condo_name ?? "",
          o.check_in ? moment(o.check_in).utc().format("ll") : "",
          o.check_out ? moment(o.check_out).utc().format("ll") : "",
          o.taxable_base !== null && o.taxable_base !== undefined
            ? fmt.format(Number(o.taxable_base))
            : "",
          o.commission !== null && o.commission !== undefined
            ? fmt.format(Number(o.commission))
            : "",
          o.taxes !== null && o.taxes !== undefined
            ? fmt.format(Number(o.taxes))
            : "",
          o.transfer_amount !== null && o.transfer_amount !== undefined
            ? fmt.format(Number(o.transfer_amount))
            : "",
        ])
      : orders.map((o) => [
          o.id,
          o.customer_name ?? "",
          o.date_order_placed
            ? moment(o.date_order_placed).tz("America/Chicago").format("lll")
            : "",
          o.package_name ?? "",
          o.room_name ?? "",
          o.check_in ? moment(o.check_in).utc().format("ll") : "",
          o.check_out ? moment(o.check_out).utc().format("ll") : "",
          o.taxable_base !== null && o.taxable_base !== undefined
            ? fmt.format(Number(o.taxable_base))
            : "",
          o.commission !== null && o.commission !== undefined
            ? fmt.format(Number(o.commission))
            : "",
          o.taxes !== null && o.taxes !== undefined
            ? fmt.format(Number(o.taxes))
            : "",
          o.resort_fee !== null && o.resort_fee !== undefined
            ? fmt.format(Number(o.resort_fee))
            : "",
          o.total !== null && o.total !== undefined
            ? fmt.format(Number(o.total))
            : "",
          o.transfer_amount !== null && o.transfer_amount !== undefined
            ? fmt.format(Number(o.transfer_amount))
            : "",
        ]);

    autoTable(doc, {
      startY: y,
      head: pdfHead,
      body: pdfBody,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [40, 40, 40] },
    });

    doc.save(
      `enjoyspi_payout-${payout.payment_id}_${moment(payout.created).format(
        "YYYY-MM-DD"
      )}.pdf`
    );
  }, [payout, orders]);

  const ActionCellRenderer = useCallback(
    (params) => {
      const orderId = params.data?.id;
      if (!orderId) return null;
      return (
        <a
          href={`/sales/orders/${orderId}`}
          onClick={(e) => {
            if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
              e.preventDefault();
              history.push(`/sales/orders/${orderId}`);
            }
          }}
          style={{ textDecoration: "none" }}
        >
          <Button
            size="small"
            component="span"
            startIcon={<OpenInNewIcon />}
            color="primary"
          >
            View Order
          </Button>
        </a>
      );
    },
    [history]
  );

  const hotelColumnDefs = useMemo(
    () => [
      {
        headerName: "Order ID",
        field: "id",
        filter: "agNumberColumnFilter",
        width: 110,
        pinned: "left",
      },
      {
        headerName: "Customer",
        field: "customer_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        minWidth: 160,
        valueFormatter: (params) =>
          params.value
            ? moment(params.value).tz("America/Chicago").format("lll")
            : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Package",
        field: "package_name",
        filter: "agTextColumnFilter",
        minWidth: 180,
      },
      {
        headerName: "Room",
        field: "room_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Taxable Base",
        field: "taxable_base",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
      },
      {
        headerName: "Commission",
        field: "commission",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Taxes",
        field: "taxes",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
      },
      {
        headerName: "Resort Fee",
        field: "resort_fee",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
      },
      {
        headerName: "Order Total",
        field: "total",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
      },
      {
        headerName: "Transfer Amount",
        field: "transfer_amount",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
      {
        headerName: "Actions",
        field: "id",
        pinned: "right",
        width: 140,
        cellRendererFramework: ActionCellRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ],
    [ActionCellRenderer]
  );

  const condoColumnDefs = useMemo(
    () => [
      {
        headerName: "Order ID",
        field: "id",
        filter: "agNumberColumnFilter",
        width: 110,
        pinned: "left",
      },
      {
        headerName: "Customer",
        field: "customer_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        minWidth: 160,
        valueFormatter: (params) =>
          params.value
            ? moment(params.value).tz("America/Chicago").format("lll")
            : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Condo",
        field: "condo_name",
        filter: "agTextColumnFilter",
        minWidth: 200,
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? moment(params.value).utc().format("ll") : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Taxable Base",
        field: "taxable_base",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
      },
      {
        headerName: "Commission",
        field: "commission",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Taxes",
        field: "taxes",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
      },
      {
        headerName: "Transfer Amount",
        field: "transfer_amount",
        type: "rightAligned",
        valueFormatter: (params) =>
          params.value !== null ? fmt.format(Number(params.value)) : "",
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
      {
        headerName: "Actions",
        field: "id",
        pinned: "right",
        width: 140,
        cellRendererFramework: ActionCellRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ],
    [ActionCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 110,
    }),
    []
  );

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error || !payout) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/sales/payouts")}
          >
            Back to Payouts
          </Button>
          <Typography color="error">
            {error?.errorMessage || "Payout not found."}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const isCondoPayout = payout.payment_type === "condo";
  const commissionPct =
    payout.hotel_commission ||
    payout.condo_commission ||
    payout.metadata?.commission_pct;

  return (
    <>
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/sales/payouts")}
          >
            Back to Payouts
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
          >
            Export Report PDF
          </Button>
        </Grid>

        {/* Payout Header */}
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Typography variant="h6">
                  Payout #{payout.payment_id}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={payout.payment_status?.toUpperCase()}
                  size="small"
                  style={{
                    backgroundColor:
                      payout.payment_status === "complete"
                        ? "#19a86dbb"
                        : "#dfad1dbb",
                    color: "white",
                    fontWeight: "bold",
                  }}
                />
              </Grid>
              {!!payout.reversed && (
                <Grid item>
                  <Chip
                    label="REVERSED"
                    size="small"
                    style={{
                      backgroundColor: "#de4141bb",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </Grid>
              )}
            </Grid>

            <Divider className={classes.divider} />

            <Grid container spacing={3} className={classes.infoGrid}>
              <Grid item xs={6} sm={3}>
                <InfoField
                  label="Date"
                  value={
                    payout.created
                      ? moment(payout.created)
                          .tz("America/Chicago")
                          .format("lll")
                      : "—"
                  }
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoField
                  label="Commission Rate"
                  value={`${commissionPct}%`}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoField label="Orders" value={orders.length} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <InfoField
                  label="Payment Type"
                  value={payout.payment_type?.toUpperCase()}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label={isCondoPayout ? "Condo" : "Hotel"}
                  value={isCondoPayout ? payout.condo_name : payout.hotel_name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Bank Account"
                  value={payout.stripe_account_name}
                />
                {payout.stripe_account_id && (
                  <Typography
                    variant="caption"
                    style={{ color: "text.secondary", display: "block" }}
                  >
                    {payout.stripe_account_id}
                  </Typography>
                )}
                {payout.stripe_account_id && (
                  <Link
                    href={`https://dashboard.stripe.com/connect/accounts/${payout.stripe_account_id}`}
                    target="_blank"
                    rel="noopener"
                    className={classes.textWithIcon}
                    style={{ marginTop: 4 }}
                  >
                    <LinkIcon fontSize="small" />
                    View Account on Stripe
                  </Link>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField
                  label="Stripe Transfer ID"
                  value={payout.stripe_transfer_id}
                />
                {payout.stripe_transfer_id && (
                  <Link
                    href={`https://dashboard.stripe.com/connect/transfers/${payout.stripe_transfer_id}`}
                    target="_blank"
                    rel="noopener"
                    className={classes.textWithIcon}
                    style={{ marginTop: 4 }}
                  >
                    <LinkIcon fontSize="small" />
                    View Transfer on Stripe
                  </Link>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoField label="Comments" value={payout.comments} />
              </Grid>
            </Grid>

            <Divider className={classes.divider} />

            {/* Financial Summary */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <InfoField
                  label="Orders Total"
                  value={fmt.format(Number(payout.total_orders_amount))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.labelText}>
                  Our Commission
                </Typography>
                <Typography className={classes.commissionValue}>
                  {fmt.format(Number(payout.commission_amount))}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.labelText}>
                  Transfer Amount
                </Typography>
                <Typography className={classes.transferValue}>
                  {fmt.format(Number(payout.total_paid))}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Orders Table */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Orders in this Payout ({orders.length})
          </Typography>
          <div
            className={
              mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
            }
            style={{
              height: Math.max(200, orders.length * 42 + 60),
              width: "100%",
            }}
          >
            <AgGridReact
              rowData={orders}
              columnDefs={isCondoPayout ? condoColumnDefs : hotelColumnDefs}
              defaultColDef={defaultColDef}
              enableCellTextSelection
              animateRows
              domLayout="normal"
            />
          </div>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="success"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => {
                  handleExportPdf();
                  setSnackbar((s) => ({ ...s, open: false }));
                }}
                disabled={isLoading || !payout}
              >
                Download PDF
              </Button>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setSnackbar((s) => ({ ...s, open: false }))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default PayoutView;
